import React, { useState, useEffect } from "react";
import "./OrderSummary.scss";
import axios from "axios";
import { API_BASE_URL } from '../config';
import { useNavigate, useLocation } from "react-router-dom";
import { useProfile } from "../Contexts/ProfileContext";
import { STORAGE_KEYS } from "../utils/storage";
import { PageLoadLoader } from "../Components/Loader";
import { useToast } from "../Contexts/ToastContext";
import { showOrderErrorToast } from "../utils/orderErrorMessage";
import {
    buildOrderSuccessSnapshot,
    finalizeCheckoutAndGoToSuccess,
    shouldBlockCheckoutRevisit,
} from "../utils/orderSuccessSnapshot";
import { fetchCoupons, resolvePendingCoupon } from "../utils/couponsApi";
import { fetchCreditsProfile } from "../utils/creditsApi";
import {
    buildOrderCreatePayload,
    buildOrderUpdatePayload,
    orderUsesStoreCredit,
} from "../utils/orderPayload";
import { payWithRazorpay } from "../api/razorpay";
import OverlayLoader from "../Components/Loader/overlay_loader";
import { isMobile } from "react-device-detect";
import { MobCouponModal } from "../Checkout/Mob/MobCouponModal";
import { useMobCheckoutLeaveConfirm } from "../hooks/useMobCheckoutLeaveConfirm";

function computeCheckoutTotals(orderSummary, selectedCoupon, useStoreCredit, creditBalance) {
    const baseAmount = Math.max(
        0,
        (orderSummary?.total_mrp || 0) - (orderSummary?.total_discount || 0)
    );
    let couponDiscountAmount = 0;
    if (selectedCoupon) {
        couponDiscountAmount =
            selectedCoupon.type === 'percentage'
                ? Math.round(baseAmount * selectedCoupon.discount / 100)
                : selectedCoupon.discount;
    }
    const afterCoupon = Math.max(0, baseAmount - couponDiscountAmount);
    const creditDiscountAmount =
        useStoreCredit && creditBalance > 0 ? Math.min(creditBalance, afterCoupon) : 0;
    const finalAmount = Math.max(0, afterCoupon - creditDiscountAmount);
    return { couponDiscountAmount, creditDiscountAmount, finalAmount };
}

export function OrderSummary() {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [creditBalance, setCreditBalance] = useState(0);
    const [useStoreCredit, setUseStoreCredit] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [deletingAddress, setDeletingAddress] = useState(false);
    const isInitialMount = React.useRef(true);
    const isGuest = localStorage.getItem('guestUser') === 'true';

    const checkoutTotals = React.useMemo(() => {
        if (!orderData?.order_summary) return null;
        return computeCheckoutTotals(
            orderData.order_summary,
            selectedCoupon,
            useStoreCredit,
            creditBalance
        );
    }, [orderData, selectedCoupon, useStoreCredit, creditBalance]);

    const [guestAddress, setGuestAddress] = useState({
        name: '',
        mobile: '',
        streetAddress: '',
        city: '',
        state: '',
        pincode: '',
        email: ''
    });

    const { LeaveConfirmModal } = useMobCheckoutLeaveConfirm(
        isMobile && processingPayment
    );

    useEffect(() => {
        const f = localStorage.getItem('guestFirstName') || '';
        const l = localStorage.getItem('guestLastName') || '';
        setGuestAddress(prev => ({ ...prev, name: `${f} ${l}`.trim() }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchCoupons();
                if (cancelled) return;
                setAvailableCoupons(list);
                const pending = await resolvePendingCoupon(list);
                if (pending && !cancelled) setSelectedCoupon(pending);
            } catch (e) {
                console.error('Failed to load coupons:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (isGuest) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const profile = await fetchCreditsProfile();
                if (!cancelled && profile) setCreditBalance(profile.balance ?? 0);
            } catch (e) {
                console.error('Failed to load store credit:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [isGuest]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!isGuest) await fetchAddresses();
                await fetchOrderData();
                if (!cancelled) setLoading(false);
            } catch (_) {
                if (!cancelled) setLoading(false);
            } finally {
                isInitialMount.current = false;
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Block revisiting checkout after order is placed (no new draft — e.g. direct URL)
    useEffect(() => {
        if (location.pathname !== '/order-summary') return;
        if (!shouldBlockCheckoutRevisit()) return;
        navigate('/', { replace: true });
    }, [location.pathname, navigate]);

    // Refetch addresses when navigating back from add-address page
    useEffect(() => {
        // Skip the initial mount (already handled by the first useEffect)
        if (!isInitialMount.current && location.pathname === '/order-summary') {
            fetchAddresses();
        }
    }, [location.pathname]);

    const fetchOrderData = async () => {
        const isGuest = localStorage.getItem('guestUser') === 'true';
        const savedOrder = isGuest
            ? localStorage.getItem('guestCheckoutData')
            : localStorage.getItem('latestOrder');
        if (savedOrder) {
            try {
                const parsedOrder = JSON.parse(savedOrder);
                setOrderData(parsedOrder);
                if (orderUsesStoreCredit(parsedOrder)) {
                    setUseStoreCredit(true);
                }

                // Optionally fetch fresh data from backend using order_id
                if (parsedOrder._id) {
                    try {
                        const token = localStorage.getItem("token");
                        const config = {
                            headers: { Authorization: `Bearer ${token}` }
                        };
                        // You can fetch by order ID if needed
                        // const response = await axios.get(`${API_BASE_URL}/orders/${parsedOrder._id}`, config);
                        // setOrderData(response.data);
                    } catch (error) {
                        console.error('Error fetching order from backend:', error);
                        // Fallback to localStorage data
                    }
                }
            } catch (e) {
                console.error('Error parsing order data:', e);
            }
        }
    };

    const fetchAddresses = async () => {
        try {
            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.get(
                '/address/get-all',
                config
            );

            const fetchedAddresses = response.data || [];
            setAddresses(fetchedAddresses);
            
            // Set default address if available, otherwise set the first one, or the newly added one
            const defaultAddress = fetchedAddresses.find(addr => addr.isDefault);
            if (defaultAddress) {
                setSelectedAddressId(defaultAddress._id);
            } else if (fetchedAddresses.length > 0) {
                // If no default, select the most recently added (last in array or newest by createdAt)
                const sortedAddresses = [...fetchedAddresses].sort((a, b) => {
                    const dateA = new Date(a.createdAt || 0);
                    const dateB = new Date(b.createdAt || 0);
                    return dateB - dateA;
                });
                setSelectedAddressId(sortedAddresses[0]._id);
            }
        } catch (error) {
            console.error('Error fetching addresses:', error);
        }
    };

    const selectedAddress = addresses.find(addr => addr._id === selectedAddressId);

    const handleAddressChange = (addressId) => {
        setSelectedAddressId(addressId);
    };

    const handleEditAddress = (addressId) => {
        navigate(`/add-address?edit=${addressId}`);
    };

    const openDeleteConfirm = (addressId, e) => {
        e.stopPropagation();
        setAddressToDelete(addressId);
    };

    const closeDeleteConfirm = () => {
        if (!deletingAddress) setAddressToDelete(null);
    };

    const handleConfirmDeleteAddress = async () => {
        if (!addressToDelete) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        setDeletingAddress(true);
        try {
            await axios.delete(`/address/${addressToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAddresses((prev) => {
                const remaining = prev.filter((addr) => addr._id !== addressToDelete);
                if (selectedAddressId === addressToDelete) {
                    const defaultAddress = remaining.find((addr) => addr.isDefault);
                    if (defaultAddress) {
                        setSelectedAddressId(defaultAddress._id);
                    } else if (remaining.length > 0) {
                        setSelectedAddressId(remaining[0]._id);
                    } else {
                        setSelectedAddressId(null);
                    }
                }
                return remaining;
            });
            setAddressToDelete(null);
            showToast({ message: "Address deleted successfully." });
        } catch (error) {
            console.error("Error deleting address:", error);
            showToast({ message: error.response?.data?.message || "Failed to delete address." });
        } finally {
            setDeletingAddress(false);
        }
    };

    const handleCouponSelect = (coupon) => {
        setSelectedCoupon(coupon);
        setShowCouponModal(false);
    };

    const handleRemoveCoupon = () => {
        setSelectedCoupon(null);
    };

    const goToOrderSuccess = (snapshot) => {
        finalizeCheckoutAndGoToSuccess(navigate, snapshot);
    };

    const buildPlaceOrderPayload = (couponDiscountAmount, creditDiscountAmount, finalAmount) => {
        const cleanedOrderItems = (orderData.order_items || []).map((item) => ({
            product_id: item.product_id,
            selectedSize: item.selectedSize,
            selectedQty: item.selectedQty,
            unit_price: item.unit_price,
            unit_mrp: item.unit_mrp,
            total_price: item.total_price,
            total_mrp: item.total_mrp,
            discount_amount: item.discount_amount,
            product_title: item.product_title || item.product_data?.title,
        }));

        return buildOrderCreatePayload({
            orderItems: cleanedOrderItems,
            orderSummary: {
                item_count: orderData.order_summary?.item_count || 0,
                total_mrp: orderData.order_summary?.total_mrp || 0,
                total_discount: orderData.order_summary?.total_discount || 0,
                coupon_discount: couponDiscountAmount,
                credit_discount: creditDiscountAmount,
                shipping_fee: 0,
                final_amount: finalAmount,
            },
            couponCode: selectedCoupon?.code || '',
            useStoreCredit,
            deliveryAddressId: selectedAddressId,
        });
    };

    const handlePlaceOrder = async () => {
        if (!orderData) {
            showToast({ message: 'Order data not found. Please try again.' });
            return;
        }

        if (isGuest) {
            const { name, mobile, streetAddress, city, state, pincode } = guestAddress;
            if (!name?.trim() || !mobile?.trim() || !streetAddress?.trim() || !city?.trim() || !state?.trim() || !pincode?.trim()) {
                showToast({ message: 'Please fill all required delivery address fields (Name, Mobile, Address, City, State, Pincode)' });
                return;
            }
        } else if (!selectedAddressId) {
            showToast({ message: 'Please select a delivery address' });
            return;
        }

        setProcessingPayment(true);

        try {
            if (isGuest) {
                const baseAmount = (orderData.order_summary?.total_mrp || 0) - (orderData.order_summary?.total_discount || 0);
                let couponDiscountAmount = 0;
                if (selectedCoupon) {
                    couponDiscountAmount = selectedCoupon.type === 'percentage'
                        ? Math.round(baseAmount * selectedCoupon.discount / 100)
                        : selectedCoupon.discount;
                }
                const finalAmount = baseAmount - couponDiscountAmount;

                const guestPayload = {
                    order_id: `guest-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    order_date: new Date().toISOString(),
                    order_status: 'placed',
                    item_count: orderData.order_summary?.item_count || 0,
                    total_mrp: orderData.order_summary?.total_mrp || 0,
                    total_discount: orderData.order_summary?.total_discount || 0,
                    coupon_discount: couponDiscountAmount,
                    final_amount: finalAmount,
                    shipping_address: {
                        name: guestAddress.name,
                        mobile: guestAddress.mobile,
                        streetAddress: guestAddress.streetAddress,
                        city: guestAddress.city,
                        state: guestAddress.state,
                        pincode: guestAddress.pincode
                    },
                    contact_email: guestAddress.email || null,
                    contact_phone: guestAddress.mobile,
                    items_summary: (orderData.order_items || []).map(item => ({
                        product_id: item.product_id,
                        title: item.product_title || item.product_data?.title || 'Product',
                        qty: item.selectedQty,
                        size: item.selectedSize,
                        unit_price: item.unit_price
                    }))
                };

                const response = await axios.post(
                    '/guest-orders',
                    guestPayload,
                    { headers: { 'Content-Type': 'application/json' } }
                );

                console.log('Guest order created:', response.data);

                localStorage.removeItem('guestCheckoutData');
                // Increment guest order count for profile header
                const currentCount = parseInt(localStorage.getItem(STORAGE_KEYS.guestOrderCount) || '0', 10);
                localStorage.setItem(STORAGE_KEYS.guestOrderCount, String(currentCount + 1));
                // Remove only the ordered items from guest cart, not the entire cart
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const orderProductKeys = (orderData.order_items || []).map(
                    (item) => `${item.product_id}-${item.selectedSize}`
                );
                const updatedGuestCart = guestCart.filter(
                    (item) => !orderProductKeys.includes(`${item.product_id}-${item.selectedSize}`)
                );
                localStorage.setItem('guestCart', JSON.stringify(updatedGuestCart));
                window.dispatchEvent(new CustomEvent('guestCartUpdated'));

                const guestSnapshot = buildOrderSuccessSnapshot({
                    orderData: {
                        ...orderData,
                        order_summary: {
                            ...orderData.order_summary,
                            coupon_discount: couponDiscountAmount,
                            final_amount: finalAmount,
                        },
                    },
                    orderId: guestPayload.order_id,
                    deliveryAddress: guestPayload.shipping_address,
                    isGuest: true,
                    couponCode: selectedCoupon?.code || '',
                    finalAmount,
                    couponDiscount: couponDiscountAmount,
                });
                goToOrderSuccess(guestSnapshot);
                return;
            }

            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const { couponDiscountAmount, creditDiscountAmount, finalAmount } = computeCheckoutTotals(
                orderData.order_summary,
                selectedCoupon,
                useStoreCredit,
                creditBalance
            );

            let placedOrder = orderData;
            let placedOrderId = orderData._id;

            if (placedOrderId) {
                const updatePayload = buildOrderUpdatePayload({
                    orderSummary: {
                        ...orderData.order_summary,
                        coupon_discount: couponDiscountAmount,
                        credit_discount: creditDiscountAmount,
                        final_amount: finalAmount,
                    },
                    couponCode: selectedCoupon?.code || '',
                    useStoreCredit,
                    deliveryAddressId: selectedAddressId,
                });

                try {
                    const updateResponse = await axios.put(
                        `${API_BASE_URL}/orders/${placedOrderId}`,
                        updatePayload,
                        config
                    );
                    placedOrder = updateResponse.data.order || updateResponse.data;
                    placedOrderId = placedOrder?._id || placedOrderId;
                } catch (updateError) {
                    console.warn('Update failed, creating order:', updateError);
                    const createResponse = await axios.post(
                        `${API_BASE_URL}/orders/create`,
                        buildPlaceOrderPayload(couponDiscountAmount, creditDiscountAmount, finalAmount),
                        config
                    );
                    placedOrder = createResponse.data.order;
                    placedOrderId = placedOrder?._id;
                }
            } else {
                const createResponse = await axios.post(
                    `${API_BASE_URL}/orders/create`,
                    buildPlaceOrderPayload(couponDiscountAmount, creditDiscountAmount, finalAmount),
                    config
                );
                placedOrder = createResponse.data.order;
                placedOrderId = placedOrder?._id;
            }

            if (!placedOrderId) {
                showToast({ message: 'Order could not be placed. Please try again.' });
                return;
            }

            const placedWithItems = {
                ...placedOrder,
                order_items: orderData.order_items || [],
            };
            setOrderData(placedWithItems);
            localStorage.setItem('latestOrder', JSON.stringify(placedWithItems));

            const paymentResult = await payWithRazorpay({
                orderId: placedOrderId,
                token,
                customer: {
                    name: addresses.find((a) => a._id === selectedAddressId)?.name || '',
                    contact: addresses.find((a) => a._id === selectedAddressId)?.mobile || '',
                },
            });

            if (!paymentResult.success) {
                if (paymentResult.cancelled) {
                    showToast({ message: 'Payment cancelled. Your order is saved as unpaid — you can try again.' });
                } else {
                    showToast({ message: paymentResult.message || 'Payment failed. Please try again.' });
                }
                return;
            }

            const paidOrder = paymentResult.order
                ? { ...placedWithItems, ...paymentResult.order, order_items: placedWithItems.order_items }
                : placedWithItems;
            setOrderData(paidOrder);
            localStorage.setItem('latestOrder', JSON.stringify(paidOrder));

            const cartResponse = await axios.post(
                `${API_BASE_URL}/orders/${placedOrderId}/clear-cart`,
                {},
                config
            );
            console.log('Cart items cleared:', cartResponse.data);

            await fetchProfile();

            const selectedAddress = addresses.find((a) => a._id === selectedAddressId);
            const successSnapshot = buildOrderSuccessSnapshot({
                orderData: {
                    ...paidOrder,
                    order_summary: {
                        ...(paidOrder.order_summary || orderData.order_summary),
                        coupon_discount: couponDiscountAmount,
                        credit_discount: creditDiscountAmount,
                        final_amount: finalAmount,
                    },
                },
                orderId: placedOrderId,
                deliveryAddress: selectedAddress || null,
                isGuest: false,
                couponCode: selectedCoupon?.code || '',
                finalAmount,
                couponDiscount: couponDiscountAmount,
                creditDiscount: creditDiscountAmount,
            });
            goToOrderSuccess(successSnapshot);

        } catch (error) {
            console.error('Error placing order:', error);
            showOrderErrorToast(showToast, error, 'Failed to place order. Please try again.');
        } finally {
            setProcessingPayment(false);
        }
    };

    if (loading) {
        return <PageLoadLoader />;
    }

    const pageContent = (
        <>
            <div className="order-summary-container">
                <div className="order-summary-header">
                    <h1>Order Summary</h1>
                    <p>Review your order details and delivery address</p>
                </div>

                <div className="order-summary-content">
                    {/* Address Selection Section */}
                    <div className="address-section">
                        <div className="section-header">
                            <h2>Delivery Address</h2>
                            {!isGuest && (
                                <button 
                                    className="add-address-btn"
                                    onClick={() => navigate('/add-address')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    Add New Address
                                </button>
                            )}
                        </div>

                        {isGuest ? (
                            <div className="guest-address-form">
                                <div className="form-row">
                                    <input
                                        type="text"
                                        placeholder="Full Name *"
                                        value={guestAddress.name}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, name: e.target.value })}
                                        className="guest-address-input"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Mobile No *"
                                        value={guestAddress.mobile}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, mobile: e.target.value })}
                                        className="guest-address-input"
                                    />
                                </div>
                                <div className="form-row">
                                    <input
                                        type="text"
                                        placeholder="Address (House No, Building, Street, Area) *"
                                        value={guestAddress.streetAddress}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, streetAddress: e.target.value })}
                                        className="guest-address-input full-width"
                                    />
                                </div>
                                <div className="form-row form-row-inline">
                                    <input
                                        type="text"
                                        placeholder="City *"
                                        value={guestAddress.city}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, city: e.target.value })}
                                        className="guest-address-input"
                                    />
                                    <input
                                        type="text"
                                        placeholder="State *"
                                        value={guestAddress.state}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, state: e.target.value })}
                                        className="guest-address-input"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Pincode *"
                                        value={guestAddress.pincode}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                                        className="guest-address-input pincode"
                                        maxLength={6}
                                    />
                                </div>
                                <div className="form-row">
                                    <input
                                        type="email"
                                        placeholder="Email (optional)"
                                        value={guestAddress.email}
                                        onChange={(e) => setGuestAddress({ ...guestAddress, email: e.target.value })}
                                        className="guest-address-input full-width"
                                    />
                                </div>
                            </div>
                        ) : addresses.length === 0 ? (
                            <div className="no-addresses">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                <p>No addresses found</p>
                                <button 
                                    className="add-address-btn primary"
                                    onClick={() => navigate('/add-address')}
                                >
                                    Add Address
                                </button>
                            </div>
                        ) : (
                            <div className="address-list">
                                {addresses.map((address) => (
                                    <div 
                                        key={address._id} 
                                        className={`address-card ${selectedAddressId === address._id ? 'selected' : ''}`}
                                        onClick={() => handleAddressChange(address._id)}
                                    >
                                        <div className="address-radio-wrapper">
                                            <input
                                                type="radio"
                                                name="address"
                                                checked={selectedAddressId === address._id}
                                                onChange={() => handleAddressChange(address._id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <div className="radio-custom"></div>
                                        </div>
                                        
                                        <div className="address-content">
                                            <div className="address-card-header">
                                                <div className="address-badges">
                                                    <span className="address-type-badge">{address.addressType}</span>
                                                    {address.isDefault && (
                                                        <span className="default-badge">Default</span>
                                                    )}
                                                </div>
                                                <div className="address-card-actions">
                                                    <button
                                                        type="button"
                                                        className="edit-address-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditAddress(address._id);
                                                        }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                            <path d="M11.333 2.667a1.414 1.414 0 0 1 2 2L5.333 12.667l-2.666.666.667-2.666L9.333 4.667a1.414 1.414 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="delete-address-btn"
                                                        onClick={(e) => openDeleteConfirm(address._id, e)}
                                                        aria-label="Delete address"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                            <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h3.066a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="address-details">
                                                <div className="address-name">{address.name}</div>
                                                <div className="address-phone">{address.mobile}</div>
                                                <div className="address-text">
                                                    {address.streetAddress}
                                                </div>
                                                <div className="address-location">
                                                    {address.city && <span>{address.city}</span>}
                                                    {address.city && address.state && <span>, </span>}
                                                    {address.state && <span>{address.state}</span>}
                                                    {address.pincode && <span> - {address.pincode}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Order Details Section */}
                    {orderData && (
                        <div className="order-details-section">
                            <h2>Order Details</h2>
                            
                            <div className="order-items">
                                {orderData.order_items?.map((item, index) => {
                                    const urls = item.product_data?.urls || item.urls;
                                    const rawImg = Array.isArray(urls) && urls.length ? (urls[1] || urls[0]) : (typeof urls === 'string' ? urls : null);
                                    const productImage = rawImg
                                        ? (String(rawImg).startsWith('http') ? rawImg : `${API_BASE_URL}/${String(rawImg).replace(/^\//, '')}`)
                                        : null;
                                    const productTitle = item.product_data?.title || item.product_title || `Product ${index + 1}`;
                                    
                                    return (
                                        <div key={index} className="order-item">
                                            {productImage && (
                                                <div className="order-item-image">
                                                    <img 
                                                        src={productImage} 
                                                        alt={productTitle}
                                                    />
                                                </div>
                                            )}
                                            <div className="order-item-main">
                                                <div className="order-item-top">
                                                    <div className="order-item-name">
                                                        {productTitle}
                                                    </div>
                                                    <div className="order-item-price">
                                                        ₹{item.total_price?.toLocaleString('en-IN') || 0}
                                                    </div>
                                                </div>
                                                <div className="order-item-meta">
                                                    <span className="order-item-size">Size: {item.selectedSize}</span>
                                                    <span className="order-item-qty">Qty: {item.selectedQty}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="order-summary-totals">
                                <div className="summary-row">
                                    <span className="summary-label">Total MRP</span>
                                    <span className="summary-value">₹{orderData.order_summary?.total_mrp?.toLocaleString('en-IN') || 0}</span>
                                </div>
                                <div className="summary-row discount">
                                    <span className="summary-label">Discount</span>
                                    <span className="summary-value discount">- ₹{orderData.order_summary?.total_discount?.toLocaleString('en-IN') || 0}</span>
                                </div>
                                <div className="summary-row coupon-row">
                                    <span className="summary-label">Coupon</span>
                                    <div className="coupon-actions">
                                        {selectedCoupon ? (
                                            <div className="applied-coupon">
                                                <span className="coupon-code">{selectedCoupon.code}</span>
                                                <button 
                                                    className="remove-coupon-btn"
                                                    onClick={handleRemoveCoupon}
                                                    title="Remove coupon"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <button 
                                                className="avail-coupon-btn"
                                                onClick={() => setShowCouponModal(true)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                </svg>
                                                Avail Coupon
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {selectedCoupon && (
                                    <div className="summary-row discount">
                                        <span className="summary-label">Coupon Discount</span>
                                        <span className="summary-value discount">
                                            - ₹{selectedCoupon.type === 'percentage' 
                                                ? Math.round((orderData.order_summary?.total_mrp || 0) * selectedCoupon.discount / 100).toLocaleString('en-IN')
                                                : selectedCoupon.discount.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                )}
                                {orderData.order_summary?.coupon_discount > 0 && !selectedCoupon && (
                                    <div className="summary-row discount">
                                        <span className="summary-label">Coupon Discount</span>
                                        <span className="summary-value discount">- ₹{orderData.order_summary.coupon_discount.toLocaleString('en-IN')}</span>
                                    </div>
                                )}
                                {!isGuest && creditBalance > 0 && (
                                    <div className="summary-row store-credit-row">
                                        <label className="store-credit-toggle">
                                            <input
                                                type="checkbox"
                                                checked={useStoreCredit}
                                                onChange={(e) => setUseStoreCredit(e.target.checked)}
                                            />
                                            <span className="summary-label">
                                                Use store credit (₹{creditBalance.toLocaleString('en-IN')} available)
                                            </span>
                                        </label>
                                    </div>
                                )}
                                {checkoutTotals?.creditDiscountAmount > 0 && (
                                    <div className="summary-row discount">
                                        <span className="summary-label">Store credit</span>
                                        <span className="summary-value discount">
                                            - ₹{checkoutTotals.creditDiscountAmount.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                )}
                                <div className="summary-row shipping">
                                    <span className="summary-label">Shipping</span>
                                    <span className="summary-value free">FREE</span>
                                </div>
                                <div className="summary-row total">
                                    <span className="summary-label">Total Amount</span>
                                    <span className="summary-value total">
                                        ₹{(checkoutTotals?.finalAmount ?? orderData.order_summary?.final_amount ?? 0).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            </div>

                            <div className="place-order-footer">
                                <button 
                                    className="place-order-btn"
                                    onClick={handlePlaceOrder}
                                    disabled={processingPayment || !orderData || (isGuest ? !(guestAddress.name?.trim() && guestAddress.mobile?.trim() && guestAddress.streetAddress?.trim() && guestAddress.city?.trim() && guestAddress.state?.trim() && guestAddress.pincode?.trim()) : !selectedAddressId)}
                                >
                                    {processingPayment ? 'PLACING ORDER...' : 'PLACE ORDER'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Coupon Modal */}
            {isMobile ? (
                <MobCouponModal
                    open={showCouponModal}
                    onClose={() => setShowCouponModal(false)}
                    coupons={availableCoupons}
                    selectedCoupon={selectedCoupon}
                    onSelect={handleCouponSelect}
                    orderTotal={orderData?.order_summary?.total_mrp || 0}
                />
            ) : showCouponModal ? (
                <div className="coupon-modal-overlay order-summary-coupon-modal" onClick={() => setShowCouponModal(false)}>
                    <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="coupon-modal-header">
                            <h2>Available Coupons</h2>
                            <button 
                                className="close-modal-btn"
                                onClick={() => setShowCouponModal(false)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
                                    <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="coupon-modal-content">
                            <div className="coupons-list">
                                {availableCoupons.map((coupon) => {
                                    const isApplicable = (orderData?.order_summary?.total_mrp || 0) >= coupon.minOrder;
                                    return (
                                        <div 
                                            key={coupon.id} 
                                            className={`coupon-card ${!isApplicable ? 'disabled' : ''} ${selectedCoupon?.id === coupon.id ? 'selected' : ''}`}
                                            onClick={() => isApplicable && handleCouponSelect(coupon)}
                                        >
                                            <div className="coupon-card-left">
                                                <div className="coupon-discount">
                                                    {coupon.type === 'percentage' ? (
                                                        <span className="discount-percentage">{coupon.discount}%</span>
                                                    ) : (
                                                        <span className="discount-amount">₹{coupon.discount}</span>
                                                    )}
                                                    <span className="discount-off">OFF</span>
                                                </div>
                                                <div className="coupon-details">
                                                    <div className="coupon-code-text">{coupon.code}</div>
                                                    <div className="coupon-description">{coupon.description}</div>
                                                    <div className="coupon-min-order">
                                                        Min. order: ₹{coupon.minOrder.toLocaleString('en-IN')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="coupon-card-right">
                                                {!isApplicable ? (
                                                    <div className="coupon-not-applicable">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M8 4v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                        </svg>
                                                        Not Applicable
                                                    </div>
                                                ) : selectedCoupon?.id === coupon.id ? (
                                                    <div className="coupon-applied">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                        Applied
                                                    </div>
                                                ) : (
                                                    <button className="apply-coupon-btn">
                                                        Apply
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            {LeaveConfirmModal}
            {addressToDelete && (
                <div className="confirm-modal-overlay" onClick={closeDeleteConfirm}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-header">
                            <h3>Delete address</h3>
                            <button type="button" className="confirm-modal-close" onClick={closeDeleteConfirm} disabled={deletingAddress} aria-label="Close">×</button>
                        </div>
                        <div className="confirm-modal-body">
                            <p>Are you sure you want to delete this address?</p>
                        </div>
                        <div className="confirm-modal-footer">
                            <button type="button" className="confirm-btn-secondary" onClick={closeDeleteConfirm} disabled={deletingAddress}>Cancel</button>
                            <button type="button" className="confirm-btn-danger" onClick={handleConfirmDeleteAddress} disabled={deletingAddress}>
                                {deletingAddress ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {processingPayment && <OverlayLoader />}
        </>
    );

    return isMobile ? (
        <div className="mob-checkout-scaling-root">{pageContent}</div>
    ) : (
        pageContent
    );
}

